import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { PROJECT_STATUSES, PRIORITIES, StatusBadge, PriorityBadge, formatCurrency, formatDate } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, Pencil, Trash2, Copy, MessageSquare, Filter, X, LayoutGrid, List, CalendarDays, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import CalendarView from '@/components/CalendarView';
import { FileUpload, FileList } from '@/components/FileManager';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import useFocusItem from '@/hooks/useFocusItem';

const INITIAL_FORM = {
  client_name: '', client_email: '', client_phone: '', project_name: '', reference: '',
  description: '', status: 'a_chiffrer', priority: 'normale', delivery_date: '',
  amount_ht: 0, cost: 0, subcontractor_name: '', subcontractor_id: '', links: [], tags: [],
  commercial_id: null, commercial_name: '', commission_pct: 0,
  client_user_id: null,
  admin_link: '', admin_note: '',
};

export default function ProductionPage() {
  const [projects, setProjects] = useState([]);
  useFocusItem(projects);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [filters, setFilters] = useState({ status: '', priority: '', search: '' });
  const [commentDialog, setCommentDialog] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [detailProject, setDetailProject] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'completed'
  const [subcontractors, setSubcontractors] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [registeredClients, setRegisteredClients] = useState([]);
  const [clientNames, setClientNames] = useState([]);
  const [qualityDialog, setQualityDialog] = useState(null);
  const [qualityForm, setQualityForm] = useState({ subcontractor_id: '', subcontractor_name: '', description: '' });
  // True when the admin chose "+ Saisie libre" — keeps the free-text input mounted even after first keystroke.
  const [customClientMode, setCustomClientMode] = useState(false);

  useEffect(() => {
    const fetchSubs = async () => {
      try {
        const { data } = await api.get('/subcontractors?active=true');
        // Defensive: filter out empty names/ids + dedupe by company_name (Radix Select crashes on duplicate values)
        const seen = new Set();
        const cleaned = (Array.isArray(data) ? data : [])
          .filter(s => s && s.id && typeof s.company_name === 'string' && s.company_name.trim() !== '')
          .filter(s => {
            const key = s.company_name.trim();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        setSubcontractors(cleaned);
      } catch { /* ignore */ }
    };
    const fetchCommercials = async () => {
      try {
        const { data } = await api.get('/commercials?active=true');
        // Defensive: filter out invalid ids and dedupe by id
        const seen = new Set();
        const cleaned = (Array.isArray(data) ? data : [])
          .filter(c => c && c.id && typeof c.company_name === 'string' && c.company_name.trim() !== '')
          .filter(c => { if (seen.has(c.id)) return false; seen.add(c.id); return true; });
        setCommercials(cleaned);
      } catch { /* ignore */ }
    };
    const fetchRegisteredClients = async () => {
      try {
        const { data } = await api.get('/clients/registered');
        // Defensive: filter out invalid user_ids and dedupe
        const seen = new Set();
        const cleaned = (Array.isArray(data) ? data : [])
          .filter(c => c && c.user_id && typeof c.company_name === 'string' && c.company_name.trim() !== '')
          .filter(c => { if (seen.has(c.user_id)) return false; seen.add(c.user_id); return true; });
        setRegisteredClients(cleaned);
      } catch { /* ignore */ }
    };
    const fetchClients = async () => {
      try {
        const { data } = await api.get('/clients/names');
        // Defensive: dedupe by company_name and filter out empty names to avoid Radix Select crash
        // (duplicate <SelectItem value=...> in the same <Select> makes Radix throw inside a portal)
        const seen = new Set();
        const cleaned = (Array.isArray(data) ? data : [])
          .filter(c => c && typeof c.company_name === 'string' && c.company_name.trim() !== '' && c.id)
          .filter(c => {
            const key = c.company_name.trim();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        setClientNames(cleaned);
      } catch { /* ignore */ }
    };
    fetchSubs();
    fetchCommercials();
    fetchRegisteredClients();
    fetchClients();
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.search) params.append('search', filters.search);
      const { data } = await api.get(`/projects?${params}`);
      setProjects(data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleSave = async () => {
    try {
      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, form);
        toast.success('Projet mis a jour');
      } else {
        await api.post('/projects', form);
        toast.success('Projet cree');
      }
      setDialogOpen(false);
      setEditingProject(null);
      setForm(INITIAL_FORM);
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    // If the existing client name isn't in the registered list, fall back to free-text mode automatically
    const isInRegisteredList = clientNames.some(c => c.company_name === project.client_name);
    setCustomClientMode(Boolean(project.client_name) && !isInRegisteredList);
    // Defensive: Radix Select v2 throws when `value` is an empty string. Coerce every
    // Select-bound field into a non-empty sentinel (or a value that MUST exist in items)
    // BEFORE setForm. Prevents the "écran noir" bug (crash inside a Radix portal that
    // sits outside our ErrorBoundary DOM subtree).
    const safeStatus = project.status && PROJECT_STATUSES[project.status] ? project.status : 'a_chiffrer';
    const safePriority = project.priority && PRIORITIES[project.priority] ? project.priority : 'normale';
    setForm({
      client_name: project.client_name || '',
      client_email: project.client_email || '',
      client_phone: project.client_phone || '',
      project_name: project.project_name || '',
      reference: project.reference || '',
      description: project.description || '',
      status: safeStatus,
      priority: safePriority,
      delivery_date: project.delivery_date ? project.delivery_date.split('T')[0] : '',
      amount_ht: project.amount_ht || 0,
      cost: project.cost || 0,
      subcontractor_name: project.subcontractor_name || '',
      subcontractor_id: project.subcontractor_id || '',
      links: Array.isArray(project.links) ? project.links : [],
      tags: Array.isArray(project.tags) ? project.tags : [],
      commercial_id: project.commercial_id || null,
      commercial_name: project.commercial_name || '',
      commission_pct: project.commission_pct || 0,
      client_user_id: project.client_user_id || null,
      admin_link: project.admin_link || '',
      admin_note: project.admin_note || '',
    });
    setDialogOpen(true);
  };

  const handleToggleCommissionPaid = async (project) => {
    const newPaid = !project.commission_paid;
    if (!window.confirm(newPaid ? `Marquer la commission de ${project.commercial_name} comme PAYÉE ?` : 'Remettre la commission en attente ?')) return;
    try {
      await api.put(`/projects/${project.id}`, { commission_paid: newPaid });
      toast.success(newPaid ? 'Commission marquée comme payée' : 'Commission remise en attente');
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce projet ?')) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success('Projet supprime');
      fetchProjects();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await api.post(`/projects/${id}/duplicate`);
      toast.success('Projet duplique');
      fetchProjects();
    } catch (err) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    try {
      await api.post(`/projects/${commentDialog.id}/comments`, { text: commentText });
      toast.success('Commentaire ajoute');
      setCommentText('');
      setCommentDialog(null);
      fetchProjects();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const handleNewProject = () => {
    setEditingProject(null);
    setForm(INITIAL_FORM);
    setCustomClientMode(false);
    setDialogOpen(true);
  };

  const openQualityFlag = (project) => {
    // Pre-select project's main subcontractor if any
    const sub = subcontractors.find(s => s.company_name === project.subcontractor_name);
    setQualityDialog(project);
    setQualityForm({
      subcontractor_id: sub?.user_id || '',
      subcontractor_name: sub?.company_name || project.subcontractor_name || '',
      description: '',
    });
  };

  const submitQualityFlag = async () => {
    if (!qualityForm.subcontractor_id) {
      toast.error('Sélectionnez un sous-traitant');
      return;
    }
    try {
      await api.post(`/projects/${qualityDialog.id}/quality`, qualityForm);
      toast.success('Problème qualité signalé');
      setQualityDialog(null);
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const markPerfect = async (project) => {
    if (!project.subcontractor_id) {
      toast.error('Aucun sous-traitant sur ce projet');
      return;
    }
    if (!window.confirm(`Marquer la livraison de "${project.project_name}" comme PARFAITE ? (+5% au score qualité de ${project.subcontractor_name})`)) return;
    try {
      await api.post(`/projects/${project.id}/quality/perfect`);
      toast.success('Livraison parfaite enregistrée (+5%)');
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const clearFilters = () => setFilters({ status: '', priority: '', search: '' });

  // Tab-based filter: active vs completed projects
  const COMPLETED_STATUSES = ['termine', 'livre', 'facture'];
  const displayProjects = projects.filter(p =>
    activeTab === 'completed'
      ? COMPLETED_STATUSES.includes(p.status)
      : !COMPLETED_STATUSES.includes(p.status)
  );
  const activeCount = projects.filter(p => !COMPLETED_STATUSES.includes(p.status)).length;
  const completedCount = projects.filter(p => COMPLETED_STATUSES.includes(p.status)).length;

  const markCompleted = async (project) => {
    if (!window.confirm(`Marquer "${project.project_name}" comme TERMINÉ ?\n\nLe projet sera déplacé dans l'onglet "Terminés" (admin et sous-traitant).`)) return;
    try {
      await api.put(`/projects/${project.id}`, { status: 'termine' });
      toast.success('Projet marqué comme terminé');
      setDetailProject(null);
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // Inline status change from the table. Optimistic UI + rollback on error.
  const handleInlineStatusChange = async (project, newStatus) => {
    if (!newStatus || newStatus === project.status) return;
    const prev = projects;
    setProjects(list => list.map(p => (p.id === project.id ? { ...p, status: newStatus } : p)));
    try {
      await api.put(`/projects/${project.id}`, { status: newStatus });
      toast.success(`Statut → ${PROJECT_STATUSES[newStatus]?.label || newStatus}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur mise à jour statut');
      setProjects(prev);
    }
  };

  // Highlight a row in red when a project has been waiting for client
  // validation for more than 5 days — reminds the admin to chase.
  // Anchored on `validation_started_at` (set by backend when status flips to
  // 'attente_validation') and NOT on `updated_at` because any unrelated PUT
  // to the project (margin, comment, etc.) would otherwise reset the age.
  const isValidationStale = (p) => {
    if (p.status !== 'attente_validation') return false;
    const ref = p.validation_started_at || p.updated_at || p.created_at;
    if (!ref) return false;
    const ageDays = (Date.now() - new Date(ref).getTime()) / (1000 * 60 * 60 * 24);
    return ageDays > 5;
  };

  const confirmOrder = async (project) => {
    if (!project.subcontractor_id) {
      toast.error('Aucun sous-traitant sur ce projet');
      return;
    }
    const already = !!project.order_confirmed_at;
    const msg = already
      ? `Renvoyer la confirmation de commande à ${project.subcontractor_name || 'sous-traitant'} ?`
      : `Envoyer le feu vert "GO PRODUCTION" à ${project.subcontractor_name || 'sous-traitant'} ?\n\nIl recevra un email + une notif dans son portail confirmant qu'il peut lancer sans autre validation.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/projects/${project.id}/confirm-order`);
      toast.success(already ? '✓ Confirmation renvoyée' : `✅ GO PRODUCTION envoyé — ${project.subcontractor_name || ''}`);
      // Refresh open detail dialog so the button label ('Renvoyer la confirmation') updates immediately
      setDetailProject(prev => (prev && prev.id === project.id)
        ? { ...prev, order_confirmed_at: data.order_confirmed_at || prev.order_confirmed_at || new Date().toISOString() }
        : prev);
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="space-y-6" data-testid="production-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Production</h1>
          <p className="text-sm text-zinc-500 mt-1">{projects.length} projet(s) — {activeCount} en cours · {completedCount} terminé(s)</p>
        </div>
        <Button data-testid="new-project-btn" onClick={handleNewProject} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
          <Plus className="w-4 h-4 mr-2" /> Nouveau projet
        </Button>
      </div>

      {/* Tabs: En cours / Terminés */}
      <div className="flex items-center gap-1 border border-zinc-800 rounded-sm p-1 bg-zinc-900 w-fit" data-testid="production-tabs">
        <button
          data-testid="tab-active"
          onClick={() => setActiveTab('active')}
          className={`px-4 py-1.5 text-sm rounded-sm transition-colors ${activeTab === 'active' ? 'bg-amber-500 text-zinc-950 font-medium' : 'text-zinc-400 hover:text-zinc-200'}`}
        >
          En cours <span className="ml-1.5 text-xs opacity-70">({activeCount})</span>
        </button>
        <button
          data-testid="tab-completed"
          onClick={() => setActiveTab('completed')}
          className={`px-4 py-1.5 text-sm rounded-sm transition-colors ${activeTab === 'completed' ? 'bg-emerald-500 text-zinc-950 font-medium' : 'text-zinc-400 hover:text-zinc-200'}`}
        >
          Terminés <span className="ml-1.5 text-xs opacity-70">({completedCount})</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-sm p-4">
        <Filter className="w-4 h-4 text-zinc-500" />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            data-testid="project-search"
            placeholder="Rechercher..."
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            className="pl-9 w-48 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm h-9 text-sm"
          />
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
          <SelectTrigger data-testid="filter-status" className="w-44 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm h-9 text-sm">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(PROJECT_STATUSES).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.priority} onValueChange={(v) => setFilters(f => ({ ...f, priority: v === 'all' ? '' : v }))}>
          <SelectTrigger data-testid="filter-priority" className="w-36 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm h-9 text-sm">
            <SelectValue placeholder="Priorite" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">Toutes</SelectItem>
            {Object.entries(PRIORITIES).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(filters.status || filters.priority || filters.search) && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-zinc-400 hover:text-zinc-100 h-9">
            <X className="w-4 h-4 mr-1" /> Effacer
          </Button>
        )}
        <div className="ml-auto flex items-center gap-1 bg-zinc-950 border border-zinc-800 rounded-sm p-0.5">
          <button data-testid="view-table" onClick={() => setViewMode('table')}
            className={`p-1.5 rounded-sm transition-colors ${viewMode === 'table' ? 'bg-zinc-800 text-amber-500' : 'text-zinc-500 hover:text-zinc-300'}`}>
            <List className="w-4 h-4" />
          </button>
          <button data-testid="view-cards" onClick={() => setViewMode('cards')}
            className={`p-1.5 rounded-sm transition-colors ${viewMode === 'cards' ? 'bg-zinc-800 text-amber-500' : 'text-zinc-500 hover:text-zinc-300'}`}>
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button data-testid="view-calendar" onClick={() => setViewMode('calendar')}
            className={`p-1.5 rounded-sm transition-colors ${viewMode === 'calendar' ? 'bg-zinc-800 text-amber-500' : 'text-zinc-500 hover:text-zinc-300'}`}>
            <CalendarDays className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Views */}
      {viewMode === 'calendar' ? (
        <CalendarView
          items={displayProjects}
          dateField="delivery_date"
          onItemClick={(item) => setDetailProject(item)}
          renderItem={(item) => (
            <span className={`${PROJECT_STATUSES[item.status]?.color?.split(' ')[1] || 'text-zinc-400'}`}>
              {item.project_name}
            </span>
          )}
        />
      ) : viewMode === 'cards' ? (
        loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <div key={i} className="h-52 bg-zinc-900 border border-zinc-800 rounded-sm nexus-shimmer" />)}
          </div>
        ) : displayProjects.length === 0 ? (
          <div className="text-center py-16 text-zinc-500">{activeTab === 'completed' ? 'Aucun projet terminé' : 'Aucun projet en cours'}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayProjects.map(p => (
              <div key={p.id} data-focus-id={p.id} className={`border rounded-sm p-5 nexus-card cursor-pointer ${p.order_accepted_at ? 'bg-emerald-950/40 border-emerald-500/40' : p.order_confirmed_at ? 'bg-emerald-950/20 border-emerald-500/25' : 'bg-zinc-900 border-zinc-800'}`} onClick={() => setDetailProject(p)} data-testid={`card-project-${p.id}`}>
                {(p.order_confirmed_at || p.order_accepted_at) && (
                  <div className={`-mx-5 -mt-5 mb-3 px-4 py-1.5 rounded-t-sm text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${p.order_accepted_at ? 'bg-emerald-500/25 text-emerald-200' : 'bg-emerald-500/12 text-emerald-300'}`}>
                    <CheckCircle2 className="w-3 h-3" />
                    {p.order_accepted_at ? 'GO — Sous-traitant a accepté' : p.order_negotiating ? 'GO envoyé — En négociation' : 'GO envoyé — En attente d\'acceptation'}
                  </div>
                )}                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-zinc-100 truncate">{p.project_name}</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">{p.client_name}</p>
                  </div>
                  <PriorityBadge priority={p.priority} />
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <StatusBadge status={p.status} statusMap={PROJECT_STATUSES} />
                  <span className="text-xs text-zinc-600 font-mono">{p.reference}</span>
                  {p.quality_issue && (
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${p.quality_resolved ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/30 animate-pulse'}`}>
                      {p.quality_resolved ? 'Qualité ✓' : 'Qualité !'}
                    </span>
                  )}
                </div>
                {p.description && <p className="text-xs text-zinc-500 mb-3 line-clamp-2">{p.description}</p>}
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-zinc-500 mb-1">
                    <span>Avancement</span>
                    <span className="text-zinc-300">{p.progress || 0}%</span>
                  </div>
                  <Progress value={p.progress || 0} className="h-1.5 bg-zinc-800" />
                </div>
                <div className="flex items-center justify-between text-xs pt-3 border-t border-zinc-800">
                  <span className="text-zinc-500">{formatDate(p.delivery_date)}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-300 font-medium">{formatCurrency(p.amount_ht)}</span>
                    <span className={p.margin >= 0 ? 'text-emerald-400' : 'text-rose-400'}>{formatCurrency(p.margin)}</span>
                  </div>
                </div>
                {(p.admin_link || p.admin_note) && (
                  <div className="mt-2 pt-2 border-t border-amber-500/10 space-y-1" onClick={e => e.stopPropagation()} data-testid={`admin-info-${p.id}`}>
                    {p.admin_link && (
                      <a
                        href={/^https?:\/\//i.test(p.admin_link) ? p.admin_link : `https://${p.admin_link}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-amber-300 hover:text-amber-200 truncate max-w-full"
                        data-testid={`admin-link-${p.id}`}
                        title={p.admin_link}
                      >
                        🔒 <span className="truncate">{p.admin_link.replace(/^https?:\/\//, '').slice(0, 50)}</span>
                      </a>
                    )}
                    {p.admin_note && (
                      <div className="text-[10px] text-amber-200/70 line-clamp-2 italic" title={p.admin_note}>
                        🔒 {p.admin_note}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-1 mt-3 pt-2 border-t border-zinc-800/50" onClick={e => e.stopPropagation()}>
                  <button onClick={() => handleEdit(p)} className="p-1.5 text-zinc-500 hover:text-zinc-100 transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => handleDuplicate(p.id)} className="p-1.5 text-zinc-500 hover:text-zinc-100 transition-colors"><Copy className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setCommentDialog(p)} className="p-1.5 text-zinc-500 hover:text-zinc-100 transition-colors"><MessageSquare className="w-3.5 h-3.5" /></button>
                  <button onClick={() => openQualityFlag(p)} title="Signaler un problème qualité" className={`p-1.5 transition-colors ${p.quality_issue ? 'text-rose-400 hover:text-rose-300' : 'text-zinc-500 hover:text-rose-400'}`}><ShieldAlert className="w-3.5 h-3.5" /></button>
                  {p.subcontractor_id && !p.quality_perfect_marked && (
                    <button onClick={() => markPerfect(p)} title="Marquer livraison parfaite (+5%)" className="p-1.5 text-zinc-500 hover:text-emerald-400 transition-colors"><CheckCircle2 className="w-3.5 h-3.5" /></button>
                  )}
                  <button onClick={() => handleDelete(p.id)} className="p-1.5 text-zinc-500 hover:text-rose-400 transition-colors ml-auto"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
      /* Table View */
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Reference</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Client</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Projet</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Statut</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Priorite</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Montant HT</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Marge</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Commission</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Livraison</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i} className="border-zinc-800/50">
                  {[...Array(10)].map((__, j) => (
                    <TableCell key={j}><div className="h-4 nexus-shimmer rounded w-20" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : displayProjects.length === 0 ? (
              <TableRow className="border-zinc-800/50">
                <TableCell colSpan={10} className="text-center py-12 text-zinc-500">
                  {activeTab === 'completed' ? 'Aucun projet terminé pour le moment.' : 'Aucun projet en cours. Cliquez sur "Nouveau projet" pour commencer.'}
                </TableCell>
              </TableRow>
            ) : (
              displayProjects.map((p) => (
                <TableRow key={p.id} data-focus-id={p.id} className={`border-zinc-800/50 nexus-table-row cursor-pointer ${isValidationStale(p) ? 'bg-rose-950/40 hover:bg-rose-950/50' : p.order_accepted_at ? 'bg-emerald-950/50 hover:bg-emerald-950/60' : p.order_confirmed_at ? 'bg-emerald-950/20 hover:bg-emerald-950/30' : ''}`} onClick={() => setDetailProject(p)}>
                  <TableCell className="text-sm text-zinc-300 font-mono">
                    {isValidationStale(p) && (
                      <span className="inline-block mr-1 text-rose-400 animate-pulse" title="Attente validation client depuis plus de 5 jours" data-testid={`stale-validation-${p.id}`}>⚠</span>
                    )}
                    {p.order_accepted_at && <CheckCircle2 className="w-3.5 h-3.5 inline mr-1 text-emerald-400" data-testid={`row-accepted-${p.id}`} />}
                    {p.order_confirmed_at && !p.order_accepted_at && <span className="inline mr-1 text-emerald-400" title="GO envoyé, en attente d'acceptation">🟢</span>}
                    {p.reference}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-300">{p.client_name}</TableCell>
                  <TableCell className="text-sm text-zinc-200 font-medium">
                    <div className="flex items-center gap-1.5">
                      <span>{p.project_name}</span>
                      {p.quality_issue && (
                        <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${p.quality_resolved ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/30 animate-pulse'}`} data-testid={`quality-badge-${p.id}`}>
                          {p.quality_resolved ? 'Qualité ✓' : 'Qualité !'}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select value={p.status} onValueChange={(v) => handleInlineStatusChange(p, v)}>
                      <SelectTrigger
                        data-testid={`inline-status-${p.id}`}
                        className={`h-7 rounded-sm text-xs font-medium border w-[168px] ${PROJECT_STATUSES[p.status]?.color || 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20'}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {Object.entries(PROJECT_STATUSES).map(([k, s]) => (
                          <SelectItem key={k} value={k} data-testid={`inline-status-option-${p.id}-${k}`}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell><PriorityBadge priority={p.priority} /></TableCell>
                  <TableCell className="text-sm text-zinc-300">{formatCurrency(p.amount_ht)}</TableCell>
                  <TableCell className={`text-sm font-medium ${p.margin >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {formatCurrency(p.margin)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {p.commercial_id ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          data-testid={`toggle-commission-${p.id}`}
                          onClick={(e) => { e.stopPropagation(); handleToggleCommissionPaid(p); }}
                          title={p.commission_paid ? 'Commission payée - cliquer pour annuler' : 'Marquer comme payée'}
                          className={`text-xs px-2 py-0.5 rounded-sm border transition-colors ${p.commission_paid ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-500/20'}`}>
                          {formatCurrency(p.commission_amount || 0)} {p.commission_paid ? '✓' : '⏳'}
                        </button>
                        <span className="text-[10px] text-zinc-600">{p.commercial_name}</span>
                      </div>
                    ) : <span className="text-zinc-700">-</span>}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-400">{formatDate(p.delivery_date)}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`edit-project-${p.id}`} onClick={() => handleEdit(p)} className="p-1.5 text-zinc-500 hover:text-zinc-100 transition-colors">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button data-testid={`duplicate-project-${p.id}`} onClick={() => handleDuplicate(p.id)} className="p-1.5 text-zinc-500 hover:text-zinc-100 transition-colors">
                        <Copy className="w-4 h-4" />
                      </button>
                      <button data-testid={`comment-project-${p.id}`} onClick={() => setCommentDialog(p)} className="p-1.5 text-zinc-500 hover:text-zinc-100 transition-colors">
                        <MessageSquare className="w-4 h-4" />
                      </button>
                      <button data-testid={`quality-flag-${p.id}`} onClick={() => openQualityFlag(p)} title="Signaler un problème qualité" className={`p-1.5 transition-colors ${p.quality_issue ? 'text-rose-400 hover:text-rose-300' : 'text-zinc-500 hover:text-rose-400'}`}>
                        <ShieldAlert className="w-4 h-4" />
                      </button>
                      {p.subcontractor_id && !p.quality_perfect_marked && (
                        <button data-testid={`mark-perfect-${p.id}`} onClick={() => markPerfect(p)} title="Marquer livraison parfaite (+5% au score qualité du ST)" className="p-1.5 text-zinc-500 hover:text-emerald-400 transition-colors">
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                      )}
                      <button data-testid={`delete-project-${p.id}`} onClick={() => handleDelete(p.id)} className="p-1.5 text-zinc-500 hover:text-rose-400 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      )}

      {/* Create/Edit Dialog */}
      <ErrorBoundary>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {editingProject ? 'Modifier le projet' : 'Nouveau projet'}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {editingProject ? 'Modifiez les informations du projet.' : 'Renseignez les informations du nouveau projet.'}
            </DialogDescription>
          </DialogHeader>
          <ErrorBoundary>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Client *</Label>
              {clientNames.length > 0 && !customClientMode ? (
                <Select
                  value={form.client_name || ''}
                  onValueChange={(v) => {
                    if (v === '__custom__') {
                      setCustomClientMode(true);
                      setForm(f => ({ ...f, client_name: '' }));
                    } else {
                      setForm(f => ({ ...f, client_name: v }));
                    }
                  }}
                >
                  <SelectTrigger data-testid="form-client-name" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm">
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {clientNames.map(c => <SelectItem key={c.id} value={c.company_name}>{c.company_name}</SelectItem>)}
                    <SelectItem value="__custom__">+ Saisie libre</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex gap-2">
                  <Input data-testid="form-client-name-custom" value={form.client_name}
                    onChange={(e) => setForm(f => ({ ...f, client_name: e.target.value }))}
                    autoFocus={customClientMode}
                    className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm flex-1"
                    placeholder="Saisir le nom du client" />
                  {clientNames.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setCustomClientMode(false); setForm(f => ({ ...f, client_name: '' })); }}
                      className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 rounded-sm h-10"
                      data-testid="form-client-name-back-to-list"
                    >
                      ← Liste
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Nom du projet *</Label>
              <Input data-testid="form-project-name" value={form.project_name} onChange={(e) => setForm(f => ({ ...f, project_name: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Email client</Label>
              <Input value={form.client_email} onChange={(e) => setForm(f => ({ ...f, client_email: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Tel client</Label>
              <Input value={form.client_phone} onChange={(e) => setForm(f => ({ ...f, client_phone: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Reference</Label>
              <Input value={form.reference} onChange={(e) => setForm(f => ({ ...f, reference: e.target.value }))}
                placeholder="Auto-genere si vide" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Sous-traitant</Label>
              <Select value={form.subcontractor_name} onValueChange={(v) => {
                if (v === 'none') {
                  setForm(f => ({ ...f, subcontractor_name: '', subcontractor_id: '' }));
                } else {
                  const sub = subcontractors.find(s => s.company_name === v);
                  setForm(f => ({ ...f, subcontractor_name: v, subcontractor_id: sub?.user_id || '' }));
                }
              }}>
                <SelectTrigger data-testid="form-subcontractor" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm">
                  <SelectValue placeholder="Aucun" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="none">Aucun</SelectItem>
                  {subcontractors.map(s => (
                    <SelectItem key={s.id} value={s.company_name}>{s.company_name}{s.specialty ? ` - ${s.specialty}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Commercial</Label>
              <Select value={form.commercial_id || 'none'} onValueChange={(v) => {
                if (v === 'none') {
                  setForm(f => ({ ...f, commercial_id: null, commercial_name: '', commission_pct: 0 }));
                } else {
                  const com = commercials.find(c => c.id === v);
                  setForm(f => ({ ...f, commercial_id: v, commercial_name: com?.company_name || '', commission_pct: com?.commission_pct || 0 }));
                }
              }}>
                <SelectTrigger data-testid="form-commercial" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm">
                  <SelectValue placeholder="Aucun" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="none">Aucun</SelectItem>
                  {commercials.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.company_name} — {c.commission_pct}%</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Client assigné (portail)</Label>
              <Select value={form.client_user_id || 'none'} onValueChange={(v) => setForm(f => ({ ...f, client_user_id: v === 'none' ? null : v }))}>
                <SelectTrigger data-testid="form-client-user" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm">
                  <SelectValue placeholder="Aucun" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="none">Aucun</SelectItem>
                  {registeredClients.map(c => (
                    <SelectItem key={c.user_id} value={c.user_id}>{c.company_name} ({c.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-zinc-600">Le client assigné verra ce projet dans son espace (ref, statut, montant HT, livraison)</p>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Statut</Label>
              <Select value={PROJECT_STATUSES[form.status] ? form.status : 'a_chiffrer'} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="form-status" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {Object.entries(PROJECT_STATUSES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Priorite</Label>
              <Select value={PRIORITIES[form.priority] ? form.priority : 'normale'} onValueChange={(v) => setForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="form-priority" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {Object.entries(PRIORITIES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Date de livraison</Label>
              <Input type="date" value={form.delivery_date} onChange={(e) => setForm(f => ({ ...f, delivery_date: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Montant HT</Label>
              <Input type="number" value={form.amount_ht} onChange={(e) => setForm(f => ({ ...f, amount_ht: parseFloat(e.target.value) || 0 }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">
                Cout sous-traitant
                {editingProject?.cost_source === 'invoice_auto' && (
                  <span className="ml-2 text-[9px] uppercase tracking-wider bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 px-1.5 py-0.5 rounded-sm" title={`Extrait automatiquement de la facture ${editingProject?.last_invoice_number || ''}`}>
                    ✨ auto (facture)
                  </span>
                )}
              </Label>
              <Input type="number" value={form.cost} onChange={(e) => setForm(f => ({ ...f, cost: parseFloat(e.target.value) || 0 }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Marge estimee</Label>
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm">
                <span className={form.amount_ht - form.cost >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                  {formatCurrency(form.amount_ht - form.cost)}
                </span>
              </div>
            </div>
            {form.commercial_id && (
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">Commission estimée ({form.commission_pct}%)</Label>
                <div className="bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm">
                  <span className="text-amber-400 font-medium">
                    {formatCurrency(Math.max(0, (form.amount_ht - form.cost) * (form.commission_pct / 100)))}
                  </span>
                </div>
              </div>
            )}
            <div className="col-span-2 space-y-2">
              <Label className="text-zinc-400 text-sm">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm min-h-[80px]" />
            </div>
            <div className="col-span-2 bg-amber-500/5 border border-amber-500/20 rounded-sm p-3 space-y-3">
              <div className="text-[11px] uppercase tracking-widest text-amber-300 font-semibold flex items-center gap-1">
                🔒 Visible par l'admin uniquement
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Lien admin (URL Drive, Dropbox, suivi fournisseur...)</Label>
                <Input
                  type="url"
                  placeholder="https://..."
                  value={form.admin_link}
                  onChange={(e) => setForm(f => ({ ...f, admin_link: e.target.value }))}
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"
                  data-testid="form-project-admin-link"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Note admin (multi-ligne, libre)</Label>
                <Textarea
                  value={form.admin_note}
                  onChange={(e) => setForm(f => ({ ...f, admin_note: e.target.value }))}
                  rows={3}
                  placeholder="Notes internes, mots de passe, suivi spécifique, négociation avec le ST..."
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"
                  data-testid="form-project-admin-note"
                />
              </div>
            </div>
          </div>

          {/* Attachments — only available once the project exists (edit mode) */}
          {editingProject && (
            <div className="bg-zinc-950/60 border border-zinc-800 rounded-sm p-4 mt-2" data-testid="edit-project-files-section">
              <Label className="text-zinc-300 text-sm font-medium mb-3 block">Pièces jointes (plans, PDF, fichiers 3D…)</Label>
              <ErrorBoundary>
                <ProjectFiles projectId={editingProject.id} />
              </ErrorBoundary>
              <p className="text-[11px] text-zinc-600 mt-2">Les fichiers sont sauvegardés immédiatement (pas besoin de cliquer sur "Mettre à jour" pour les conserver).</p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">
              Annuler
            </Button>
            <Button data-testid="save-project-btn" onClick={handleSave} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
              {editingProject ? 'Mettre a jour' : 'Creer le projet'}
            </Button>
          </DialogFooter>
          </ErrorBoundary>
        </DialogContent>
      </Dialog>
      </ErrorBoundary>

      {/* Comment Dialog */}
      <Dialog open={!!commentDialog} onOpenChange={() => setCommentDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="comment-dialog">
          <DialogHeader>
            <DialogTitle>Commentaires - {commentDialog?.project_name}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">Historique des commentaires du projet.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {commentDialog?.comments?.length > 0 ? (
              commentDialog.comments.map((c, i) => (
                <div key={i} className="bg-zinc-950 border border-zinc-800 rounded-sm p-3">
                  <div className="flex justify-between text-xs text-zinc-500 mb-1">
                    <span className="font-medium">{c.author}</span>
                    <span>{formatDate(c.date)}</span>
                  </div>
                  <p className="text-sm text-zinc-300">{c.text}</p>
                </div>
              ))
            ) : (
              <p className="text-sm text-zinc-500 text-center py-2">Aucun commentaire</p>
            )}
          </div>
          <div className="flex gap-2 mt-2">
            <Textarea
              data-testid="comment-input"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Ajouter un commentaire..."
              className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm min-h-[60px]"
            />
          </div>
          <DialogFooter>
            <Button data-testid="submit-comment" onClick={handleAddComment} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailProject} onOpenChange={() => setDetailProject(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="project-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {detailProject?.project_name}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">Détails complets du projet.</DialogDescription>
          </DialogHeader>
          {detailProject && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-zinc-500">Client:</span> <span className="text-zinc-200 ml-2">{detailProject.client_name}</span></div>
                <div><span className="text-zinc-500">Reference:</span> <span className="text-zinc-200 ml-2 font-mono">{detailProject.reference}</span></div>
                <div><span className="text-zinc-500">Statut:</span> <span className="ml-2"><StatusBadge status={detailProject.status} statusMap={PROJECT_STATUSES} /></span></div>
                <div><span className="text-zinc-500">Priorite:</span> <span className="ml-2"><PriorityBadge priority={detailProject.priority} /></span></div>
                <div><span className="text-zinc-500">Montant HT:</span> <span className="text-zinc-200 ml-2">{formatCurrency(detailProject.amount_ht)}</span></div>
                <div><span className="text-zinc-500">Cout:</span> <span className="text-zinc-200 ml-2">{formatCurrency(detailProject.cost)}</span></div>
                <div><span className="text-zinc-500">Marge:</span> <span className={`ml-2 font-medium ${detailProject.margin >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{formatCurrency(detailProject.margin)} ({detailProject.margin_pct}%)</span></div>
                <div><span className="text-zinc-500">Livraison:</span> <span className="text-zinc-200 ml-2">{formatDate(detailProject.delivery_date)}</span></div>
                {detailProject.subcontractor_name && <div><span className="text-zinc-500">Sous-traitant:</span> <span className="text-zinc-200 ml-2">{detailProject.subcontractor_name}</span></div>}
                {detailProject.client_email && <div><span className="text-zinc-500">Email:</span> <span className="text-zinc-200 ml-2">{detailProject.client_email}</span></div>}
              </div>
              {detailProject.description && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Description</p>
                  <p className="text-sm text-zinc-300 bg-zinc-950 border border-zinc-800 rounded-sm p-3">{detailProject.description}</p>
                </div>
              )}
              {detailProject.history?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Historique</p>
                  <div className="space-y-2">
                    {detailProject.history.map((h, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-zinc-400">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        <span>{h.action}</span> <span className="text-zinc-600">par {h.user}</span> <span className="text-zinc-600 ml-auto">{formatDate(h.date)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <ProjectFiles projectId={detailProject.id} />
              {detailProject.subcontractor_id && (
                <div className="pt-2 border-t border-zinc-800 flex flex-wrap items-center gap-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold w-full">Actions sous-traitant</div>
                  <Button
                    data-testid="confirm-order-btn"
                    onClick={() => confirmOrder(detailProject)}
                    className={detailProject.order_confirmed_at
                      ? "bg-emerald-950 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-900 rounded-sm text-sm"
                      : "bg-emerald-500 text-white hover:bg-emerald-400 rounded-sm text-sm font-semibold"}
                  >
                    {detailProject.order_confirmed_at ? <CheckCircle2 className="w-4 h-4 mr-1.5" /> : <span className="mr-1.5">🟢</span>}
                    {detailProject.order_confirmed_at ? 'Renvoyer la confirmation' : 'Confirmer la commande — GO'}
                  </Button>
                  {detailProject.order_confirmed_at && (
                    <span className="text-[11px] text-emerald-400 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Confirmée le {new Date(detailProject.order_confirmed_at).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </div>
              )}
              {!['termine', 'livre', 'facture'].includes(detailProject.status) && (
                <div className="pt-2 border-t border-zinc-800 flex justify-end">
                  <Button
                    data-testid="mark-completed-btn"
                    onClick={() => markCompleted(detailProject)}
                    className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 rounded-sm font-medium"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Marquer comme terminé
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Quality Flag Dialog */}
      <Dialog open={!!qualityDialog} onOpenChange={() => setQualityDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="quality-flag-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-rose-400" /> Signaler un problème qualité
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {qualityDialog?.project_name} — {qualityDialog?.client_name}
              <br />
              <span className="text-rose-400 text-xs">Le sous-traitant assigné perdra 10% de score qualité.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Sous-traitant à imputer *</Label>
              <Select value={qualityForm.subcontractor_id} onValueChange={(v) => {
                const s = subcontractors.find(s2 => s2.user_id === v);
                setQualityForm(f => ({ ...f, subcontractor_id: v, subcontractor_name: s?.company_name || '' }));
              }}>
                <SelectTrigger data-testid="quality-form-subcontractor" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {subcontractors.filter(s => s.user_id).map(s => (
                    <SelectItem key={s.id} value={s.user_id}>{s.company_name} (score: {s.quality_score ?? 100}%)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Description du problème</Label>
              <Textarea data-testid="quality-form-description" value={qualityForm.description}
                onChange={(e) => setQualityForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Ex: dimensions hors tolérance sur la pièce X, traitement de surface incorrect..."
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm min-h-[100px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQualityDialog(null)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
            <Button data-testid="quality-flag-submit" onClick={submitQualityFlag} className="bg-rose-500/10 text-rose-400 border border-rose-500/30 hover:bg-rose-500/20 rounded-sm font-medium">
              <ShieldAlert className="w-4 h-4 mr-1.5" /> Signaler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProjectFiles({ projectId }) {
  const [files, setFiles] = useState([]);
  const fetchFiles = useCallback(async () => {
    try {
      const { data } = await api.get(`/files/list?entity_type=project&entity_id=${projectId}`);
      setFiles(data);
    } catch (err) { console.debug('[ProjectFiles] list failed:', err?.message); }
  }, [projectId]);
  useEffect(() => { if (projectId) fetchFiles(); }, [projectId, fetchFiles]);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-zinc-500 uppercase tracking-wider">Pièces jointes ({files.length})</p>
        <FileUpload entityType="project" entityId={projectId} onUploadComplete={fetchFiles} />
      </div>
      <FileList files={files} onDelete={fetchFiles} />
    </div>
  );
}
